/** @module components/searchInput */
import baustein from "baustein";
import _find from "lodash/find";
import analytics from "web/script/analytics/analytics";
import "web/script/components/auto-complete";
import forms from "web/script/dom/forms";
import globals from "web/script/modules/globals";
import { getSearchPageURL } from "web/script/routing/routes";
import searchMixin from "./mixins/search-mixin";

// Selectors
var GENDER_SELECT_SELECTOR = ".search-input__gender-select";
var GENDER_SELECT_TEXT_SELECTOR = GENDER_SELECT_SELECTOR + "__text";

var GENDER_SELECT_SHOW_MODIFIER = GENDER_SELECT_SELECTOR.replace(/^\./, "") + "--show";

// Analytics category
var EVENT_CATEGORY = "search_input";

/**
 * The autosuggest - wraps the search input and autosuggest.
 * @constructor
 * @alias module:components/searchInput
 * @extends module:baustein
 */
export default baustein.register("search-input", {
    /** @lends module:components/searchInput.prototype */
    mixins: [searchMixin],
    tagName: "form",

    setupEvents: function (add) {
        add("change", '[name="meta_gender"]', this.onGenderSelectChange);
    },

    /**
     * Handles change events from the gender picker. If the search input has a value at the time
     * of the change event being received then the search is submitted.
     * @param {Event} event
     * @param {HTMLSelectElement} select
     */
    onGenderSelectChange: function (event, select) {
        var label = this.el.querySelector(GENDER_SELECT_TEXT_SELECTOR);
        var selectedOption = _find(select.options, { selected: true });
        label.textContent = selectedOption.textContent;

        analytics.event(EVENT_CATEGORY, "change_gender", selectedOption.value);

        if (this.getSearchQuery().length > 0) {
            this.submitSearch();
        }
    },

    /**
     * Get the current gender from the select input.
     */
    getCurrentGender: function () {
        var select = this.el.querySelector('[name="meta_gender"]');
        var currentGender = _find(select.options, { selected: true });
        return currentGender ? currentGender.value : null;
    },

    /**
     * When the search input is focused we will make sure that the auto-complete component knows
     * the current search term and then show it.
     */
    onFocus: function () {
        var autoComplete = this._getAutoComplete();
        var query = this.getSearchQuery();

        analytics.event(EVENT_CATEGORY, "search_input_focused");

        autoComplete.updateOptions({
            query: query,
            gender: this.getCurrentGender(),
        });
        this._showAutoComplete();

        this.el.querySelector(GENDER_SELECT_SELECTOR).classList.add(GENDER_SELECT_SHOW_MODIFIER);

        this.setSearchTerm(query);
        this.setGlobalHandler("click", this.onGlobalClick);
    },

    /**
     * Global click event handler. If the click was outside of the component this should close
     * the auto-complete, otherwise it should not do anything.
     * @param  {Event} event
     */
    onGlobalClick: function (event) {
        if (this.el.contains(event.target)) {
            return;
        }

        this._hideAutoComplete();

        if (this.getSearchQuery() === "") {
            this.el
                .querySelector(GENDER_SELECT_SELECTOR)
                .classList.remove(GENDER_SELECT_SHOW_MODIFIER);
        }

        this.releaseGlobalHandler("click", this.onGlobalClick);
    },

    /**
     * Handler for the user selecting a suggestion in the auto-complete component.
     * @param {object} event The suggestions object passed as the event from the auto-complete component.
     */
    onSuggestionSelect: function (event) {
        var analyticsAction = this._getAnalyticsEventAction("click", event.suggestion);

        this.setSearchQuery(this._getQueryFromSuggestion(event));

        if (event.suggestion.type === "query") {
            return analytics.event(EVENT_CATEGORY, analyticsAction, event.index).then(() => {
                this.submitSearch();
            });
        } else if (["category", "profile"].includes(event.suggestion.type)) {
            return analytics.event(EVENT_CATEGORY, analyticsAction).then(function () {
                globals.window.location = event.suggestion.path;
            });
        }
    },

    /**
     * Handles the submit event of the form.
     * @param {Event} event
     */
    onSubmit: function (event) {
        var activeSuggestion = this._getAutoComplete().getActiveSuggestion();
        var eventAction;

        event.preventDefault();

        // Don't submit empty searches.
        if (this.getSearchQuery().length === 0) {
            return;
        }

        if (
            activeSuggestion &&
            ["category", "profile"].includes(activeSuggestion.suggestion.type)
        ) {
            eventAction = this._getAnalyticsEventAction("enter", activeSuggestion.suggestion);

            analytics.event(EVENT_CATEGORY, eventAction).then(function () {
                globals.window.location = activeSuggestion.suggestion.path;
            });

            return;
        } else if (activeSuggestion) {
            eventAction = this._getAnalyticsEventAction("enter", activeSuggestion.suggestion);
            analytics.event(EVENT_CATEGORY, eventAction, activeSuggestion.index.toString());
        } else {
            let label = "without_suggestions";

            if (this._getAutoComplete().isShowingSuggestions()) {
                label = "with_suggestions";
            }
            analytics.event(EVENT_CATEGORY, "submit_search_input", label);
        }
        this.submitSearch();
    },

    /**
     * Submits the current search by pushing a new state into the browsers history.
     * Also saves the submitted search term for future use.
     */
    submitSearch: function () {
        var form = this.el;
        var params;

        params = forms.serialize(form);
        params.q = params.q.trim();
        params.term = params.term.trim();

        var autoComplete = this._getAutoComplete();
        var gender = this.getCurrentGender();
        autoComplete.updateOptions({
            query: params.q,
            gender: gender,
        });
        this._hideAutoComplete();

        this.emit("submitSearch");

        let target = new URL(getSearchPageURL(), globals.window.location.href);
        target.searchParams.set("q", params.q);
        target.searchParams.set("term", params.term);
        target.searchParams.set("meta_gender", gender);
        globals.window.location = target.href;
    },

    /**
     * Generates the 'action' parameter for event tracking.
     * @param {String} action i.e 'click' or 'enter'
     * @param {Object} suggestion An object which has a key called `type` i.e('query' or 'profile')
     */
    _getAnalyticsEventAction: function (action, suggestion) {
        var type;

        if (this.getSearchTerm() === "") {
            type = "previous_search_term";
        } else if (suggestion.type) {
            type = `${suggestion.type}_suggestion`;
        }

        return action + "_on_" + type;
    },
});
